/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';

import { isValidData, renderSkeleton } from '../../../data/utils';
import { ApiReducer, ApiScope, ApplicationState } from '../../../data/interfaces';
import CopyCommand from "./CopyCommand";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    apiDocsTitle: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiScopesTitleBox
    },
    apiInfoTitleHead: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiScopesTitleText
    },
    chip: {
        margin: theme.spacing(0.5),
        ...theme.custom.apiOverviewPage.tabs.overview.apiScopesChip
    },
    noScopesMessage: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiScopesNoScopesMessage
    },
    scopesGrid: {
        ...theme.custom.apiOverviewPage.tabs.overview.scopesGrid
    },
}));

interface ApiScopesProps {
    apiReducer: ApiReducer
}

function ApiScopes ({ apiReducer }: ApiScopesProps) {

    const classes = useStyles();
    const { loading: apiLoading, api } = apiReducer;
    const scopesData: string[] = [];
    
    if (isValidData(api)) {
        for (let index = 0; index < api.scopes.length; index++) {
            scopesData.push(api.scopes[index].key);
        }
    }

    const scopeDataSpacedString = scopesData.join(" ");

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm container className={classes.apiDocsTitle}>
                <Grid item xs lg={12} className={classes.scopesGrid}>
                    <Typography className={classes.apiInfoTitleHead}>
                        <FormattedMessage
                            id="api.apiOverview.apiScopes.labels.scopes"
                            defaultMessage="Scopes"
                        />
                    </Typography>
                    {scopesData.length > 0  && (
                        <Grid>
                            <CopyCommand copyCmd={scopeDataSpacedString}/>
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Grid item xs={12} sm container lg={12}>
                {
                    apiLoading ?
                        renderSkeleton("rect", "100%", 25)
                        :
                        scopesData.length === 0 ?
                            <Typography className={classes.noScopesMessage}>
                                <FormattedMessage
                                    id="api.apiOverview.apiScopes.messages.noScopes"
                                    defaultMessage="No scopes available"
                                />
                            </Typography>
                            :
                            scopesData.map((scope, key) => {
                                return (
                                        <Chip
                                            key={key}
                                            label={scope}
                                            className={classes.chip}
                                        />
                                );
                            })
                }
            </Grid>
        </Grid>
    )
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiReducer: state.apiReducer
    }
}

export default connect(mapStateToProps)(ApiScopes);
